import groupBy from 'lodash/groupBy';

import { Logger } from '@eluve/logger';
import { bytesToMB } from '@eluve/utils';

import { AppointmentFileIdbRow } from './files-db';
import { AppointmentFileRow } from './opfs';

export interface AppointmentFileInfo {
  appointmentId: string;
  numFiles: number;
  files: {
    fileName: string;
    ageHours: number;
    fileSizeMB: number;
    attemptCount: number;
    lastAttemptedAt?: string;
    isOrphaned: boolean; // Exists in OPFS but not in DB
    wasDegraded?: boolean;
  }[];
}

export interface FileAnalysis {
  appointmentFiles: AppointmentFileInfo[];
  appointmentCount: number;
  fileCount: number;
  orphanedFileCount?: number;
  oldestFileAgeHours?: number;
}

// Calculate file age in hours based on last modified timestamp
const calculateFileAge = (file: { lastModified: number }): number => {
  try {
    const ageMs = Date.now() - file.lastModified;
    return Math.round((ageMs / (1000 * 60 * 60)) * 100) / 100; // Hours with 2 decimals
  } catch {
    return 0;
  }
};

/**
 * Analyzes local appointment audio files and their corresponding IDB records.
 * Groups files by appointment, calculates file statistics such as file count, orphaned files, and oldest file age.
 * @param opfsFiles - Array of OPFS files.
 * @param dbRecords - Array of IDB records.
 * @returns FileAnalysis object containing grouped appointment files and statistics.
 */
export const extractFileStats = (
  opfsFiles: AppointmentFileRow[],
  dbRecords: AppointmentFileIdbRow[],
  logger: Logger,
): FileAnalysis => {
  if (!opfsFiles.length) {
    return {
      appointmentFiles: [],
      fileCount: 0,
      orphanedFileCount: 0,
      oldestFileAgeHours: 0,
      appointmentCount: 0,
    };
  }

  try {
    const appointmentGroups = groupBy(opfsFiles, (file) => file.appointmentId);
    const appointmentFiles: AppointmentFileInfo[] = Object.entries(
      appointmentGroups,
    ).map(([appointmentId, files]) => {
      return {
        appointmentId,
        numFiles: files.length,
        files: files.map((file) => {
          const dbRecord = dbRecords.find((db) => db.id === file.name);
          const ageHours = calculateFileAge(file.file);
          return {
            fileName: file.name,
            ageHours,
            fileSizeMB: bytesToMB(file.file.size),
            attemptCount: dbRecord?.attemptCount ?? 0,
            lastAttemptedAt: dbRecord?.lastUploadAttemptedAt,
            isOrphaned: !dbRecord,
            wasDegraded: dbRecord?.wasDegraded ?? false,
          };
        }),
      };
    });

    const allApptFiles = appointmentFiles.flatMap((appt) => appt.files);
    const orphanedFileCount = allApptFiles.filter(
      (file) => file.isOrphaned,
    ).length;
    const oldestFileAgeHours =
      allApptFiles.length > 0
        ? Math.max(...allApptFiles.map((file) => file.ageHours))
        : 0;

    return {
      appointmentFiles,
      appointmentCount: appointmentFiles.length,
      fileCount: allApptFiles.length,
      orphanedFileCount,
      oldestFileAgeHours,
    };
  } catch (e) {
    logger.warn('Failed to analyze appointment files', { error: e });
    return {
      appointmentFiles: [],
      fileCount: 0,
      orphanedFileCount: 0,
      oldestFileAgeHours: 0,
      appointmentCount: 0,
    };
  }
};
